export type Waveform = 'sine' | 'saw' | 'pulse' | 'triangle' | 'wavetable';

/** Web Audio PeriodicWave-compatible Fourier coefficients; index 0 is DC. */
export interface HarmonicWavetable {
  real: ArrayLike<number>;
  imag: ArrayLike<number>;
}

export interface OscillatorOptions {
  sampleRate: number;
  waveform: Waveform;
  frequency: number;
  phase?: number;
  pulseWidth?: number;
  phaseDistortion?: number;
  reversePhase?: boolean;
  invertPolarity?: boolean;
  wavetable?: HarmonicWavetable;
}

const MIN_PULSE_WIDTH = 0.01;
const MAX_PULSE_WIDTH = 0.99;
const MAX_PHASE_DISTORTION = 0.95;
const WAVEFORMS: ReadonlySet<string> = new Set(['sine', 'saw', 'pulse', 'triangle', 'wavetable']);
const WAVETABLE_SIZE = 4_096;
const MAX_WAVETABLE_HARMONICS = WAVETABLE_SIZE / 2 - 1;

interface WavetableLayer {
  readonly maxHarmonic: number;
  readonly samples: Float64Array;
}

const assertFinite = (name: string, value: number): void => {
  if (!Number.isFinite(value)) throw new RangeError(`${name} must be finite`);
};

const assertPhase = (phase: number): void => {
  assertFinite('phase', phase);
  if (phase < 0 || phase >= 1) throw new RangeError('phase must be in [0, 1)');
};

const wrapPhase = (phase: number): number => phase - Math.floor(phase);

/** Smooth, monotonic phase warp. Zero is exactly the legacy linear phase. */
const distortPhase = (phase: number, amount: number): number =>
  wrapPhase(phase + (amount * Math.sin(2 * Math.PI * phase)) / (2 * Math.PI));

/** Polynomial correction around a unit step discontinuity. */
const polyBlep = (phase: number, phaseIncrement: number): number => {
  if (phaseIncrement <= 0) return 0;
  if (phase < phaseIncrement) {
    const t = phase / phaseIncrement;
    return t + t - t * t - 1;
  }
  if (phase > 1 - phaseIncrement) {
    const t = (phase - 1) / phaseIncrement;
    return t * t + t + t + 1;
  }
  return 0;
};

const correctedPulse = (phase: number, phaseIncrement: number, pulseWidth: number): number => {
  let value = phase < pulseWidth ? 1 : -1;
  value += polyBlep(phase, phaseIncrement);
  value -= polyBlep(wrapPhase(phase - pulseWidth), phaseIncrement);
  const dc = 2 * pulseWidth - 1;
  const peakAfterCentering = 2 * Math.max(pulseWidth, 1 - pulseWidth);
  return (value - dc) / peakAfterCentering;
};

const correctedReversePulse = (phase: number, phaseIncrement: number, pulseWidth: number): number => {
  const reversePhase = phase === 0 ? 1 : 1 - phase;
  let value = reversePhase < pulseWidth ? 1 : -1;
  value -= polyBlep(phase, phaseIncrement);
  value += polyBlep(wrapPhase(phase - (1 - pulseWidth)), phaseIncrement);
  const dc = 2 * pulseWidth - 1;
  const peakAfterCentering = 2 * Math.max(pulseWidth, 1 - pulseWidth);
  return (value - dc) / peakAfterCentering;
};

const buildWavetableLayers = (coefficients: HarmonicWavetable): readonly WavetableLayer[] => {
  const realLength = coefficients.real.length;
  const imagLength = coefficients.imag.length;
  if (!Number.isInteger(realLength) || realLength !== imagLength) {
    throw new RangeError('wavetable real and imag arrays must have the same length');
  }
  if (realLength < 2 || realLength > MAX_WAVETABLE_HARMONICS + 1) {
    throw new RangeError(`wavetable must contain 1 to ${MAX_WAVETABLE_HARMONICS} harmonics`);
  }
  const real = new Float64Array(realLength);
  const imag = new Float64Array(realLength);
  for (let i = 0; i < realLength; i += 1) {
    const realCoefficient = coefficients.real[i] as number;
    const imagCoefficient = coefficients.imag[i] as number;
    assertFinite(`wavetable real[${i}]`, realCoefficient);
    assertFinite(`wavetable imag[${i}]`, imagCoefficient);
    real[i] = realCoefficient;
    imag[i] = imagCoefficient;
  }
  if (real[0] !== 0 || imag[0] !== 0) throw new RangeError('wavetable DC coefficients must be zero');

  let highestHarmonic = 0;
  let coefficientPeak = 0;
  for (let harmonic = 1; harmonic < real.length; harmonic += 1) {
    coefficientPeak = Math.max(
      coefficientPeak,
      Math.abs(real[harmonic] as number),
      Math.abs(imag[harmonic] as number),
    );
    if (real[harmonic] !== 0 || imag[harmonic] !== 0) highestHarmonic = harmonic;
  }
  if (highestHarmonic === 0) throw new RangeError('wavetable requires at least one non-zero harmonic');

  const limits: number[] = [];
  for (let limit = 1; limit < highestHarmonic; limit *= 2) limits.push(limit);
  if (limits[limits.length - 1] !== highestHarmonic) limits.push(highestHarmonic);

  const layers: WavetableLayer[] = [];
  let globalPeak = 0;
  for (const maxHarmonic of limits) {
    const samples = new Float64Array(WAVETABLE_SIZE);
    for (let index = 0; index < WAVETABLE_SIZE; index += 1) {
      const phase = index / WAVETABLE_SIZE;
      let sample = 0;
      for (let harmonic = 1; harmonic <= maxHarmonic; harmonic += 1) {
        const angle = 2 * Math.PI * harmonic * phase;
        sample += ((real[harmonic] as number) / coefficientPeak) * Math.cos(angle)
          + ((imag[harmonic] as number) / coefficientPeak) * Math.sin(angle);
      }
      samples[index] = sample;
      globalPeak = Math.max(globalPeak, Math.abs(sample));
    }
    layers.push({ maxHarmonic, samples });
  }

  // One bank-wide gain preserves relative layer loudness. Linear interpolation
  // is a convex combination, so normalized table points guarantee headroom.
  const gain = 1 / globalPeak;
  for (const layer of layers) {
    for (let index = 0; index < WAVETABLE_SIZE; index += 1) {
      layer.samples[index] = (layer.samples[index] as number) * gain;
    }
  }
  return layers;
};

/**
 * Realtime-safe oscillator kernel. No objects or arrays are allocated by
 * nextSample/processInto. Saw and pulse discontinuities use PolyBLEP.
 */
export class BandLimitedOscillator {
  readonly sampleRate: number;
  private waveform: Waveform;
  private frequency: number;
  private phase: number;
  private pulseWidth: number;
  private phaseDistortion: number;
  private reversePhase: boolean;
  private invertPolarity: boolean;
  private triangleState: number;
  private wavetableLayers: readonly WavetableLayer[];

  constructor(options: OscillatorOptions) {
    assertFinite('sampleRate', options.sampleRate);
    if (options.sampleRate <= 0) throw new RangeError('sampleRate must be greater than zero');
    this.sampleRate = options.sampleRate;
    this.waveform = 'sine';
    this.frequency = 0;
    this.phase = 0;
    this.pulseWidth = 0.5;
    this.phaseDistortion = 0;
    this.reversePhase = false;
    this.invertPolarity = false;
    this.triangleState = -1;
    this.wavetableLayers = [];
    if (options.wavetable) this.setWavetable(options.wavetable);
    this.setWaveform(options.waveform);
    this.setFrequency(options.frequency);
    this.setPulseWidth(options.pulseWidth ?? 0.5);
    this.setPhaseDistortion(options.phaseDistortion ?? 0);
    this.setReversePhase(options.reversePhase ?? false);
    this.setInvertPolarity(options.invertPolarity ?? false);
    this.reset(options.phase ?? 0);
  }

  setFrequency(frequency: number): void {
    assertFinite('frequency', frequency);
    if (frequency < 0 || frequency >= this.sampleRate / 2) {
      throw new RangeError('frequency must be in [0, sampleRate / 2)');
    }
    this.frequency = frequency;
  }

  setWaveform(waveform: Waveform): void {
    if (!WAVEFORMS.has(waveform)) throw new RangeError('waveform is not supported');
    if (waveform === 'wavetable' && this.wavetableLayers.length === 0) {
      throw new RangeError('wavetable waveform requires harmonic wavetable coefficients');
    }
    this.waveform = waveform;
  }

  /** Rebuilds and atomically installs an owned, normalized mip-map bank. */
  setWavetable(wavetable: HarmonicWavetable): void {
    const layers = buildWavetableLayers(wavetable);
    this.wavetableLayers = layers;
  }

  setPulseWidth(pulseWidth: number): void {
    assertFinite('pulseWidth', pulseWidth);
    if (pulseWidth < MIN_PULSE_WIDTH || pulseWidth > MAX_PULSE_WIDTH) {
      throw new RangeError(`pulseWidth must be in [${MIN_PULSE_WIDTH}, ${MAX_PULSE_WIDTH}]`);
    }
    this.pulseWidth = pulseWidth;
  }

  setPhaseDistortion(phaseDistortion: number): void {
    assertFinite('phaseDistortion', phaseDistortion);
    if (phaseDistortion < -MAX_PHASE_DISTORTION || phaseDistortion > MAX_PHASE_DISTORTION) {
      throw new RangeError(`phaseDistortion must be in [-${MAX_PHASE_DISTORTION}, ${MAX_PHASE_DISTORTION}]`);
    }
    this.phaseDistortion = phaseDistortion;
  }

  setReversePhase(reversePhase: boolean): void {
    if (typeof reversePhase !== 'boolean') throw new TypeError('reversePhase must be boolean');
    this.reversePhase = reversePhase;
  }

  setInvertPolarity(invertPolarity: boolean): void {
    if (typeof invertPolarity !== 'boolean') throw new TypeError('invertPolarity must be boolean');
    this.invertPolarity = invertPolarity;
  }

  reset(phase = 0): void {
    assertPhase(phase);
    this.phase = phase;
    const distortion = this.waveform === 'pulse' || this.waveform === 'wavetable' ? 0 : this.phaseDistortion;
    const warpedPhase = distortPhase(phase, distortion);
    const orientedPhase = wrapPhase(this.reversePhase ? -warpedPhase : warpedPhase);
    this.triangleState = 4 * Math.abs(orientedPhase - 0.5) - 1;
  }

  nextSample(): number {
    const phaseIncrement = this.frequency / this.sampleRate;
    const phase = this.phase;
    const distortion = this.waveform === 'pulse' || this.waveform === 'wavetable' ? 0 : this.phaseDistortion;
    const warpedPhase = distortPhase(phase, distortion);
    const orientedPhase = wrapPhase(this.reversePhase ? -warpedPhase : warpedPhase);
    const warpedIncrement = phaseIncrement * (1 + distortion * Math.cos(2 * Math.PI * phase));
    let output: number;

    switch (this.waveform) {
      case 'sine':
        output = Math.sin(2 * Math.PI * orientedPhase);
        break;
      case 'saw': {
        const sawPhase = this.reversePhase && warpedPhase === 0 ? 1 : orientedPhase;
        const correction = polyBlep(warpedPhase, Math.abs(warpedIncrement));
        output = 2 * sawPhase - 1 + (this.reversePhase ? correction : -correction);
        break;
      }
      case 'pulse':
        output = this.reversePhase
          ? correctedReversePulse(phase, phaseIncrement, this.pulseWidth)
          : correctedPulse(phase, phaseIncrement, this.pulseWidth);
        break;
      case 'triangle': {
        const square = correctedPulse(warpedPhase, Math.abs(warpedIncrement), 0.5);
        this.triangleState += 4 * warpedIncrement * square;
        this.triangleState -= Math.abs(warpedIncrement) * this.triangleState;
        output = 0.45 * this.triangleState;
        break;
      }
      case 'wavetable': {
        const harmonicLimit = phaseIncrement === 0
          ? Number.POSITIVE_INFINITY
          : Math.floor(0.5 / phaseIncrement);
        let layer = this.wavetableLayers[0] as WavetableLayer;
        for (let index = 1; index < this.wavetableLayers.length; index += 1) {
          const candidate = this.wavetableLayers[index] as WavetableLayer;
          if (candidate.maxHarmonic > harmonicLimit) break;
          layer = candidate;
        }
        const tablePosition = orientedPhase * WAVETABLE_SIZE;
        const firstIndex = Math.floor(tablePosition);
        const fraction = tablePosition - firstIndex;
        const secondIndex = (firstIndex + 1) % WAVETABLE_SIZE;
        const first = layer.samples[firstIndex] as number;
        output = first + fraction * ((layer.samples[secondIndex] as number) - first);
        break;
      }
    }

    if (this.invertPolarity) output = -output;
    this.phase = wrapPhase(phase + phaseIncrement);
    return output;
  }

  processInto(output: Float32Array, offset = 0, length = output.length - offset): void {
    if (!Number.isInteger(offset) || offset < 0 || offset > output.length) {
      throw new RangeError('offset must be an integer inside the output array');
    }
    if (!Number.isInteger(length) || length < 0 || offset + length > output.length) {
      throw new RangeError('length must be a non-negative integer inside the output array');
    }
    const end = offset + length;
    for (let i = offset; i < end; i += 1) output[i] = this.nextSample();
  }
}
